import { CloudUpload, XCircle } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";

export default function ImageUploadWithPreview(props) {
    const imgRef = useRef<HTMLInputElement>(null)
    const { multiple = false, onPick } = props;
    const prevRef = useRef<{ files: FileList | null }>({ files: null }).current
    const [imagePreviews, setImagePreviews] = useState<string[] | null>(null);
    const [dragActive, setDragActive] = useState(false);

    const handleFile = (file: File) => {
        if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => setImagePreviews([reader.result as string]);
            reader.readAsDataURL(file);
            onPick?.(file);
            addFile(file, false)
        }
    };

    function addFile(files, multiple = true) {
        const dt = new DataTransfer();
        if (multiple) {
            Array.from(files).forEach((file: File) => dt.items.add(file));
        } else {
            dt.items.add(files)
        }
        prevRef.files = dt.files;
    }

    function removeFile(indexToRemove) {
        const dt = new DataTransfer();
        Array.from(prevRef.files).forEach((file, index) => {
            if (index !== indexToRemove) {
                dt.items.add(file);
            }
        });

        prevRef.files = dt.files;
        imgRef.current.files = dt.files;
        onPick?.(dt.files);
    }

    const handleMultipleFile = (files: FileList) => {
        if (files?.length) {
            for (let i = 0; i < files.length; ++i) {
                const file = files[i];
                const reader = new FileReader();
                reader.onloadend = () => setImagePreviews(s => (s ? [...s, reader.result as string] : [reader.result as string]));
                reader.readAsDataURL(file);
            }
            onPick?.(files);
            addFile(files)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files || [];
        if (files?.length) {
            for (let i = 0; i < files.length; ++i) {
                const file = files[i];
                const reader = new FileReader();
                reader.onloadend = () => setImagePreviews(s => (s ? [...s, reader.result as string] : [reader.result as string]));
                reader.readAsDataURL(file);
            }
            const val = multiple ? files : files?.[0]
            onPick?.(val);
            addFile(val, multiple)
        } else {
            setImagePreviews(null);
        }
    };

    const handleRemove = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        delete imagePreviews[index];
        const newImgPreview = imagePreviews.filter(e => e);
        setImagePreviews(newImgPreview);
        removeFile(index);
    }

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0] && !multiple) {
            handleFile(e.dataTransfer.files[0]);
        }

        if (e.dataTransfer.files && e.dataTransfer.files[0] && multiple) {
            handleMultipleFile(e.dataTransfer.files);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };



    const renderImgPreview = useCallback((imgPreview, index) => (<div key={`${imgPreview}`} className='relative'>
        <XCircle onClick={(e) => handleRemove(e, index)} className="z-10 w-6 h-6 right-1 top-1 absolute text-gray-400 cursor-pointer" />
        <img
            src={imgPreview}
            alt="Preview"
            className="h-full object-contain rounded-md max-w-xs"
        />
    </div>), [imagePreviews])

    return (
        <div>
            <label
                htmlFor="dropzone-file"
                className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-md cursor-pointer transition ${dragActive ? "border-blue-100 bg-blue-50" : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                {imagePreviews?.length ? <div className="flex flex-row gap-3 h-full">
                    {imagePreviews.map((imgPreview, i) => renderImgPreview(imgPreview, i))}
                </div> : (
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <CloudUpload />
                        <p className="text-sm text-gray-500">
                            <span className="font-medium">Drop files here</span> or click to upload.
                        </p>
                    </div>
                )}
                <input
                    id="dropzone-file"
                    type="file"
                    multiple={multiple}
                    ref={imgRef}
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                />
            </label>
        </div>
    );
}
